
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";
import {
  WizardAttachment,
  WizardClientConfig,
  WizardMessage,
  WizardMode,
  getWizardAttachmentUrl,
  getWizardConfig,
  uploadWizardAttachment,
  WizardAttachmentExpiredError,
} from "@/flow/services/wizard.service";
import type {
  WizardConnectionPrompt,
  WizardContextLevel,
} from "@/flow/services/wizard.service";
import WizardContextRing from "@/flow/components/wizard/WizardContextRing.vue";
import {
  renderWizardMarkdown,
  sanitizeWizardAssistantMessage,
} from "@/flow/utils/wizardMarkdown";
import i18n from "@/core/i18n/i18n";
import { ElTooltip } from "element-plus";
import { TYPE } from "vue-toastification";
import { showAlert } from "@/core/popup";

interface WizardChatProps {
  messages: WizardMessage[];
  loading: boolean;
  sessionId: string | null;
  canStop: boolean;
  statusMessage: string;
  statusDetailMessage: string;
  interruptionNotice: string;
  mode: WizardMode;
  ensureUploadSessionId: () => string;
  contextLevel: WizardContextLevel | null;
  contextMenuOpen: boolean;
  compacting: boolean;
  contextError: string;
  connectionPrompt: WizardConnectionPrompt | null;
}

interface PendingAttachment {
  localId: string;
  id?: string;
  name: string;
  contentType: string;
  size: number;
  kind: "image" | "document";
  status: "uploading" | "ready" | "error";
  error?: string;
  file: File;
  /** Set for paste-to-file chips so the text can be restored to the input. */
  rawText?: string;
}

const WIZARD_SUGGESTIONS = [
  "Get all assets from OTMM",
  "Sync Jira issues to a database",
  "Upload files from SFTP to SharePoint",
];

const CHAT_SUGGESTIONS = [
  "Which connector should I use for SAP to SharePoint?",
  "Why is my Jira action failing?",
  "How would you model retries for this integration?",
];

const LOADING_FALLBACKS = [
  "Planning...",
  "OneTeging...",
  "Brewing...",
  "Refining...",
  "Connecting...",
  "Shaping...",
];

/**
 * Append a " (n)" disambiguator before the file extension, mirroring the
 * browser/OS convention for same-named files (e.g. "report (2).pdf"). Used only
 * for display + the @-mention token; the real filename is unchanged.
 */
function appendNameIndex(name: string, n: number): string {
  const dot = name.lastIndexOf(".");
  return dot > 0
    ? `${name.slice(0, dot)} (${n})${name.slice(dot)}`
    : `${name} (${n})`;
}

export default defineComponent({
  name: "WizardChat",
  components: { ElTooltip, WizardContextRing },
  props: {
    messages: {
      type: Array as PropType<WizardMessage[]>,
      default: () => [],
    },
    loading: { type: Boolean, default: false },
    sessionId: { type: String, default: null },
    canStop: { type: Boolean, default: false },
    statusMessage: { type: String, default: "" },
    statusDetailMessage: { type: String, default: "" },
    interruptionNotice: { type: String, default: "" },
    mode: {
      type: String as PropType<WizardMode>,
      default: "wizard",
    },
    ensureUploadSessionId: {
      type: Function as PropType<() => string>,
      required: true,
    },
    contextLevel: {
      type: Object as PropType<WizardContextLevel | null>,
      default: null,
    },
    contextMenuOpen: { type: Boolean, default: false },
    compacting: { type: Boolean, default: false },
    contextError: { type: String, default: "" },
    connectionPrompt: {
      type: Object as PropType<WizardConnectionPrompt | null>,
      default: null,
    },
  },
  emits: [
    "send",
    "stop",
    "toggle-context",
    "close-context",
    "compact-context",
    "open-connection-setup",
    "dismiss-connection-setup",
  ],
  setup(props, { emit }) {
    const typedProps = props as WizardChatProps;
    const { t } = i18n.global;

    /** Compact "when was this attached" hint for the @-mention picker. */
    function formatAttachedAt(iso?: string): string {
      if (!iso) return t("TeggyAssistant.JustAdded");
      const ts = new Date(iso).getTime();
      if (Number.isNaN(ts)) return "";
      const minutes = Math.floor((Date.now() - ts) / 60000);
      if (minutes < 1) return t("TeggyAssistant.JustNow");
      if (minutes < 60) return t("TeggyAssistant.MinutesAgo", { n: minutes });
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t("TeggyAssistant.HoursAgo", { n: hours });
      const days = Math.floor(hours / 24);
      if (days < 7) return t("TeggyAssistant.DaysAgo", { n: days });
      return new Date(ts).toLocaleDateString();
    }

    const inputText = ref("");
    const messagesContainer = ref<HTMLElement | null>(null);
    const inputRef = ref<HTMLTextAreaElement | null>(null);
    const fileInputRef = ref<HTMLInputElement | null>(null);
    const loadingIndex = ref(0);
    let loadingTimer: number | null = null;

    const pendingAttachments = ref<PendingAttachment[]>([]);
    const attachmentNotice = ref("");
    const config = ref<WizardClientConfig | null>(null);
    let localIdCounter = 0;

    // @-mention file picker state.
    const mentionOpen = ref(false);
    const mentionQuery = ref("");
    const mentionActiveIndex = ref(0);
    const mentionStart = ref(0); // index of the '@' in inputText
    // Files the user explicitly @-mentioned this turn (authoritative by id —
    // tolerates duplicate filenames via a unique display token). Not reactive;
    // only read at send time. `att` carries the REAL filename for the backend;
    // `token` is the unique "@displayName" inserted into the text.
    interface SelectedMention {
      att: WizardAttachment;
      token: string;
    }
    const selectedMentions = new Map<string, SelectedMention>();

    const DEFAULT_PASTE_THRESHOLD = 5000;
    const DEFAULT_MESSAGE_MAX = 5000;

    const isUploading = computed(() =>
      pendingAttachments.value.some((a) => a.status === "uploading")
    );

    const readyAttachments = computed(() =>
      pendingAttachments.value.filter((a) => a.status === "ready" && a.id)
    );

    const canSend = computed(
      () =>
        !isUploading.value &&
        (inputText.value.trim().length > 0 || readyAttachments.value.length > 0)
    );

    const acceptAttr = computed(() => {
      const types = config.value?.attachmentAllowedContentTypes;
      return types && types.length ? types.join(",") : "";
    });

    // Single source of truth for duplicate-name disambiguation, shown EVERYWHERE
    // a file appears (composer chip, sent message card, @-mention picker) so the
    // same file always reads the same. Browser/Finder convention: the FIRST file
    // with a given name keeps it as-is; later duplicates get " (1)", " (2)"… in
    // upload order (earlier turns first, then the current composer). Keyed by id.
    const displayNameById = computed<Record<string, string>>(() => {
      const ordered: { id: string; name: string }[] = [];
      const seen = new Set<string>();
      const push = (id?: string, name?: string): void => {
        if (id && name !== undefined && !seen.has(id)) {
          seen.add(id);
          ordered.push({ id, name });
        }
      };
      typedProps.messages.forEach((m) =>
        (m.attachments || []).forEach((a) => push(a.id, a.name))
      );
      readyAttachments.value.forEach((a) => push(a.id, a.name));

      const counts = new Map<string, number>();
      const result: Record<string, string> = {};
      ordered.forEach(({ id, name }) => {
        const i = counts.get(name) ?? 0;
        counts.set(name, i + 1);
        // i===0 → first occurrence keeps the plain name; i>=1 → " (i)".
        result[id] = i === 0 ? name : appendNameIndex(name, i);
      });
      return result;
    });

    // Pool of files that can be @-mentioned: current ready chips + files attached
    // in earlier turns (from message history), de-duplicated by id. `displayName`
    // comes from the shared map above so the picker, chips and cards agree; the
    // inserted "@token" uses it too so duplicates stay unique. `attachedAt` (the
    // message time for earlier files) is the "which one" hint.
    interface MentionFile extends WizardAttachment {
      source: "attached" | "earlier";
      displayName: string;
      attachedAt?: string;
    }
    const sessionFiles = computed<MentionFile[]>(() => {
      const names = displayNameById.value;
      const byId = new Map<string, MentionFile>();
      readyAttachments.value.forEach((a) => {
        if (a.id) {
          byId.set(a.id, {
            id: a.id,
            name: a.name,
            contentType: a.contentType,
            size: a.size,
            kind: a.kind,
            source: "attached",
            displayName: names[a.id] ?? a.name,
          });
        }
      });
      typedProps.messages.forEach((m) => {
        (m.attachments || []).forEach((a) => {
          if (a.id && !byId.has(a.id)) {
            byId.set(a.id, {
              ...a,
              source: "earlier",
              displayName: names[a.id] ?? a.name,
              attachedAt: m.timestamp,
            });
          }
        });
      });
      // Array.from, not spread: the production build downlevels iterable spread into a
      // helper that sums `.length` of its arguments, and a Map iterator has none — the
      // result is Array(NaN), which throws "RangeError: Invalid array length". Native
      // spread handles iterators, so this only fails in built bundles, not dev.
      return Array.from(byId.values());
    });

    const mentionResults = computed<MentionFile[]>(() => {
      const q = mentionQuery.value.toLowerCase();
      const list = sessionFiles.value;
      const matched = q
        ? list.filter((f) => f.displayName.toLowerCase().includes(q))
        : list;
      return matched.slice(0, 8);
    });

    const suggestions = computed(() =>
      typedProps.mode === "chat" ? CHAT_SUGGESTIONS : WIZARD_SUGGESTIONS
    );

    const welcomeTitle = computed(() =>
      typedProps.mode === "chat" ? "AI Chat" : "AI Flow Builder"
    );

    const welcomeSubtitle = computed(() =>
      typedProps.mode === "chat"
        ? "Ask about connectors, troubleshoot actions, or plan an integration before you build."
        : "Describe your integration needs and I'll create the flow for you."
    );

    const inputPlaceholder = computed(() => {
      if (typedProps.loading) {
        return t("TeggyAssistant.PlaceholderWaiting");
      }

      if (typedProps.mode === "chat") {
        return t("TeggyAssistant.PlaceholderChat");
      }

      return t("TeggyAssistant.PlaceholderCreator");
    });

    // The same button is send or stop depending on `loading`, so its tooltip and
    // accessible name have to follow the icon rather than being fixed strings.
    const sendButtonLabel = computed(() =>
      typedProps.loading
        ? t("TeggyAssistant.StopResponse")
        : t("TeggyAssistant.SendPrompt")
    );

    const loadingWord = computed(
      () => LOADING_FALLBACKS[loadingIndex.value % LOADING_FALLBACKS.length]
    );

    function stopLoadingRotation(): void {
      if (loadingTimer !== null) {
        window.clearInterval(loadingTimer);
        loadingTimer = null;
      }
    }

    function startLoadingRotation(): void {
      stopLoadingRotation();
      loadingTimer = window.setInterval(() => {
        loadingIndex.value += 1;
      }, 3200);
    }

    function scrollToBottom(): void {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
        }
      });
    }

    function messageMaxChars(): number {
      return config.value?.messageMaxChars ?? DEFAULT_MESSAGE_MAX;
    }

    function pasteThreshold(): number {
      return config.value?.pasteToFileThreshold ?? DEFAULT_PASTE_THRESHOLD;
    }

    function nextLocalId(): string {
      localIdCounter += 1;
      return `att-${Date.now()}-${localIdCounter}`;
    }

    // ----- @-mention picker --------------------------------------------------
    function closeMention(): void {
      mentionOpen.value = false;
    }

    // Detect an "@token" immediately before the caret and open/filter the popup.
    function updateMention(): void {
      const el = inputRef.value;
      if (!el) return;
      const caret = el.selectionStart ?? inputText.value.length;
      const before = inputText.value.slice(0, caret);
      // Allow spaces in the token (filenames can contain them, e.g.
      // "Generated Image … .png"); exclude only "@" and newlines, and bound the
      // length. False positives self-resolve because the popup only shows when
      // the query actually matches a file.
      const match = before.match(/(?:^|\s)@([^@\n]{0,100})$/);
      if (match && sessionFiles.value.length > 0) {
        const [, query] = match;
        // Only reset the highlighted row when the query changes or the popup is
        // (re)opening — otherwise arrow-key navigation would snap back to 0 on
        // every keyup.
        if (!mentionOpen.value || query !== mentionQuery.value) {
          mentionActiveIndex.value = 0;
        }
        mentionQuery.value = query;
        mentionStart.value = caret - query.length - 1; // index of '@'
        mentionOpen.value = true;
      } else {
        mentionOpen.value = false;
      }
    }

    function onInput(): void {
      autoResize();
      updateMention();
    }

    function onKeydown(event: KeyboardEvent): void {
      if (mentionOpen.value && mentionResults.value.length > 0) {
        const n = mentionResults.value.length;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          mentionActiveIndex.value = (mentionActiveIndex.value + 1) % n;
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          mentionActiveIndex.value = (mentionActiveIndex.value - 1 + n) % n;
          return;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          selectMention(mentionResults.value[mentionActiveIndex.value]);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          closeMention();
          return;
        }
      }
      // Popup closed: Enter (without Shift) sends; Shift+Enter = newline.
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    }

    function selectMention(file: MentionFile): void {
      const el = inputRef.value;
      const caret = el?.selectionStart ?? inputText.value.length;
      const beforeAt = inputText.value.slice(0, mentionStart.value);
      const after = inputText.value.slice(caret);
      // Insert the unique display name so two same-named files get distinct
      // tokens; the real filename still goes to the backend via `att`.
      const insert = `@${file.displayName} `;
      inputText.value = beforeAt + insert + after;
      if (file.id) {
        selectedMentions.set(file.id, {
          att: {
            id: file.id,
            name: file.name,
            contentType: file.contentType,
            size: file.size,
            kind: file.kind,
          },
          token: `@${file.displayName}`,
        });
      }
      closeMention();
      nextTick(() => {
        autoResize();
        const pos = (beforeAt + insert).length;
        if (el) {
          el.focus();
          el.setSelectionRange(pos, pos);
        }
      });
    }

    async function send(): Promise<void> {
      if (typedProps.loading || isUploading.value) return;

      let text = inputText.value.trim();

      // Typed-overflow guard: convert an over-long body into a text attachment so
      // the inline cap never blocks the user.
      if (text.length > messageMaxChars()) {
        const file = new File([text], "Message text.txt", {
          type: "text/plain",
        });
        addFiles([file], text);
        await waitForUploads();
        text = "";
      }

      const ready = readyAttachments.value;

      // Scope: send freshly-uploaded chips + any @-mentioned files still
      // referenced in the text (incl. re-pulling an earlier-turn file by id).
      const rawText = inputText.value;
      const metaById = new Map<string, WizardAttachment>();
      ready.forEach((a) => {
        metaById.set(a.id as string, {
          id: a.id as string,
          name: a.name,
          contentType: a.contentType,
          size: a.size,
          kind: a.kind,
        });
      });
      // Match each mention by its unique inserted token, longest-token-first and
      // consuming the matched span. This prevents a shorter token from
      // false-matching inside a longer one — e.g. "@report" must not match inside
      // "@report (1)" (extensionless duplicates) or "@doc" inside "@document". A
      // mention is only re-attached if its exact token still occupies its own span
      // in the text. Send the real metadata, not the display-suffixed name.
      let remaining = rawText;
      // Array.from rather than spread — see the note in the mention list above.
      Array.from(selectedMentions.entries())
        .sort((a, b) => b[1].token.length - a[1].token.length)
        .forEach(([id, mention]) => {
          const idx = remaining.indexOf(mention.token);
          if (idx !== -1) {
            metaById.set(id, mention.att);
            // Blank out this occurrence so a prefix token can't re-match it.
            remaining =
              remaining.slice(0, idx) +
              " ".repeat(mention.token.length) +
              remaining.slice(idx + mention.token.length);
          }
        });

      const attachmentIds = Array.from(metaById.keys());
      const attachments: WizardAttachment[] = Array.from(metaById.values());

      if (!text && attachmentIds.length === 0) return;

      emit("send", { text, attachmentIds, attachments });
      inputText.value = "";
      pendingAttachments.value = [];
      selectedMentions.clear();
      attachmentNotice.value = "";
      closeMention();
      if (inputRef.value) {
        inputRef.value.style.height = "auto";
      }
    }

    function sendSuggestion(suggestion: string): void {
      emit("send", { text: suggestion, attachmentIds: [], attachments: [] });
    }

    function waitForUploads(): Promise<void> {
      return new Promise((resolve) => {
        if (!isUploading.value) {
          resolve();
          return;
        }
        let unwatch = () => undefined as void;
        unwatch = watch(isUploading, (uploading) => {
          if (!uploading) {
            unwatch();
            resolve();
          }
        });
      });
    }

    function openFilePicker(): void {
      attachmentNotice.value = "";
      const input = fileInputRef.value;
      if (input) {
        input.click();
      }
    }

    function isTypeAllowed(contentType: string): boolean {
      const types = config.value?.attachmentAllowedContentTypes;
      if (!types || !types.length) return true;
      return types.includes(contentType);
    }

    function addFiles(files: File[], rawText?: string): void {
      const maxPer = config.value?.attachmentMaxPerMessage ?? 10;
      const maxBytes = config.value?.attachmentMaxFileBytes ?? 25 * 1024 * 1024;

      const remaining = Math.max(0, maxPer - pendingAttachments.value.length);
      if (files.length > remaining) {
        attachmentNotice.value = t("TeggyAssistant.MaxFilesNotice", {
          count: maxPer,
        });
      }

      files.slice(0, remaining).forEach((file) => {
        const contentType = file.type || "application/octet-stream";
        if (!isTypeAllowed(contentType)) {
          attachmentNotice.value = t("TeggyAssistant.UnsupportedType", {
            name: file.name,
          });
        } else if (file.size > maxBytes) {
          attachmentNotice.value = t("TeggyAssistant.FileTooLarge", {
            name: file.name,
            limit: formatSize(maxBytes),
          });
        } else {
          const entry: PendingAttachment = {
            localId: nextLocalId(),
            name: file.name,
            contentType,
            size: file.size,
            kind: contentType.startsWith("image/") ? "image" : "document",
            status: "uploading",
            file,
            rawText,
          };
          pendingAttachments.value.push(entry);
          // Pass the REACTIVE array element (not the raw `entry`) so status
          // mutations inside uploadOne trigger re-renders. Mutating the raw
          // object bypasses Vue's proxy and the chip would stay "uploading".
          const stored =
            pendingAttachments.value[pendingAttachments.value.length - 1];
          uploadOne(stored).catch(() => undefined);
        }
      });
    }

    async function uploadOne(entry: PendingAttachment): Promise<void> {
      try {
        const uploadSessionId = typedProps.ensureUploadSessionId();
        const result = await uploadWizardAttachment(
          uploadSessionId,
          entry.file
        );
        entry.id = result.id;
        entry.name = result.name;
        entry.contentType = result.contentType;
        entry.size = result.size;
        entry.kind = result.kind;
        entry.status = "ready";
      } catch (error: any) {
        entry.status = "error";
        entry.error =
          error?.response?.data?.detail ||
          error?.message ||
          t("TeggyAssistant.UploadFailed");
        attachmentNotice.value = t("TeggyAssistant.CouldNotUpload", {
          name: entry.name,
        });
      }
    }

    function onFilesPicked(event: Event): void {
      const input = event.target as HTMLInputElement;
      const files = input.files ? Array.from(input.files) : [];
      if (files.length) {
        addFiles(files);
      }
      // Reset so picking the same file again re-triggers change.
      input.value = "";
    }

    function removeAttachment(localId: string): void {
      pendingAttachments.value = pendingAttachments.value.filter(
        (a) => a.localId !== localId
      );
      // Clear the (shared) notice — otherwise a stale "Could not upload …"
      // message lingers after the failed chip is removed. Drop it once no
      // errored chips remain.
      if (!pendingAttachments.value.some((a) => a.status === "error")) {
        attachmentNotice.value = "";
      }
    }

    function revertPasted(att: PendingAttachment): void {
      if (att.rawText === undefined) return;
      inputText.value = inputText.value
        ? `${inputText.value}\n${att.rawText}`
        : att.rawText;
      removeAttachment(att.localId);
      nextTick(autoResize);
    }

    function onPaste(event: ClipboardEvent): void {
      const clipboard = event.clipboardData;
      if (!clipboard) return;

      // Clipboard files (e.g. screenshots) become attachments directly.
      const files = clipboard.files ? Array.from(clipboard.files) : [];
      if (files.length) {
        event.preventDefault();
        addFiles(files);
        return;
      }

      const text = clipboard.getData("text/plain");
      if (text && text.length > pasteThreshold()) {
        event.preventDefault();
        const file = new File([text], "Pasted content.txt", {
          type: "text/plain",
        });
        addFiles([file], text);
      }
    }

    function stop(): void {
      if (!typedProps.loading || !typedProps.canStop) {
        return;
      }

      emit("stop");
    }

    function autoResize(): void {
      if (inputRef.value) {
        inputRef.value.style.height = "auto";
        inputRef.value.style.height = `${Math.min(
          inputRef.value.scrollHeight,
          120
        )}px`;
      }
    }

    function formatMessage(message: WizardMessage): string {
      const content =
        typedProps.mode === "wizard" && message.role === "assistant"
          ? sanitizeWizardAssistantMessage(message.content)
          : message.content;
      return renderWizardMarkdown(content);
    }

    function formatSize(bytes: number): string {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    function iconForType(contentType: string): string {
      const type = (contentType || "").toLowerCase();
      if (type.startsWith("image/")) return "file-image";
      if (type === "application/pdf") return "file-pdf";
      if (type.includes("word")) return "file-word";
      if (type === "application/json") return "file-code";
      if (type.startsWith("text/")) return "file-alt";
      return "file";
    }

    /** Trigger a browser download of a sent attachment's original file. */
    // Pending object-URL revocations created by downloadSent (local-fallback
    // blobs). Each is revoked after the click lands (60s), or eagerly on unmount
    // if that hasn't fired yet — otherwise an early unmount would leak the blob
    // URL and its timer.
    const pendingDownloadRevokes = new Set<{
      revoke: () => void;
      timer: number;
    }>();

    async function downloadSent(att: WizardAttachment): Promise<void> {
      const activeSessionId = typedProps.sessionId;
      if (!activeSessionId) return;
      try {
        const resolved = await getWizardAttachmentUrl(activeSessionId, att.id);
        const link = document.createElement("a");
        link.href = resolved.url;
        link.download = att.name || "attachment";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Object URLs (local fallback) must be revoked after the click lands.
        // Track the pending revoke so unmount can reclaim it if the timer hasn't
        // fired yet.
        if (resolved.revoke) {
          const { revoke } = resolved;
          const entry = { revoke, timer: 0 };
          entry.timer = window.setTimeout(() => {
            revoke();
            pendingDownloadRevokes.delete(entry);
          }, 60000);
          pendingDownloadRevokes.add(entry);
        }
      } catch (error) {
        // Surface a toast (app convention for a failed download — cf.
        // Flow.ProcessLogDownloadError): the download button lives on a sent card
        // that may be scrolled anywhere, so a position-independent toast fits
        // better than the composer-anchored inline notice. console.error aids
        // debugging; the card stays so the user can retry.
        console.error("[wizard-attach] download failed", error);
        // An expired/removed blob can't be retried — tell the user to re-upload
        // instead of the generic "please try again" message.
        const messageKey =
          error instanceof WizardAttachmentExpiredError
            ? "TeggyAssistant.AttachmentExpired"
            : "TeggyAssistant.DownloadFailed";
        showAlert(TYPE.ERROR, t(messageKey));
      }
    }

    watch(
      () => typedProps.messages.length,
      () => {
        scrollToBottom();
      }
    );

    watch(
      () => typedProps.loading,
      (isLoading) => {
        if (isLoading) {
          startLoadingRotation();
        } else {
          stopLoadingRotation();
        }
        scrollToBottom();
      },
      { immediate: true }
    );

    watch(
      () => typedProps.statusMessage,
      () => {
        scrollToBottom();
      }
    );

    watch(
      () => typedProps.statusDetailMessage,
      () => {
        scrollToBottom();
      }
    );

    watch(
      () => typedProps.interruptionNotice,
      () => {
        scrollToBottom();
      }
    );

    watch(
      () => typedProps.connectionPrompt,
      () => {
        scrollToBottom();
      }
    );

    onMounted(() => {
      getWizardConfig()
        .then((cfg) => {
          config.value = cfg;
        })
        .catch(() => {
          // Backend unreachable — fall back to built-in defaults silently.
        });
    });

    onBeforeUnmount(() => {
      stopLoadingRotation();
      // Reclaim any object URLs still waiting on their 60s revoke timer.
      pendingDownloadRevokes.forEach((entry) => {
        window.clearTimeout(entry.timer);
        entry.revoke();
      });
      pendingDownloadRevokes.clear();
    });

    return {
      formatMessage,
      inputPlaceholder,
      inputRef,
      fileInputRef,
      inputText,
      loadingWord,
      sendButtonLabel,
      messagesContainer,
      send,
      sendSuggestion,
      stop,
      autoResize,
      suggestions,
      welcomeSubtitle,
      welcomeTitle,
      // attachments
      pendingAttachments,
      attachmentNotice,
      canSend,
      acceptAttr,
      openFilePicker,
      onFilesPicked,
      onPaste,
      removeAttachment,
      revertPasted,
      iconForType,
      formatSize,
      displayNameById,
      downloadSent,
      // @-mention picker
      mentionOpen,
      mentionResults,
      mentionActiveIndex,
      onKeydown,
      onInput,
      updateMention,
      closeMention,
      selectMention,
      formatAttachedAt,
      t,
    };
  },
});
